import type { SortingState } from '@tanstack/angular-table';
import {
  ORDER_DIRECTION,
  type OrderClause,
  type OrderDirection,
} from '../../../query-dsl';

/**
 * Converts TanStack SortingState to the Aurora Query DSL `order` block with a
 * deterministic fallback column. The fallback prevents non-deterministic
 * pagination when rows share the same value in the sorted column (e.g., bulk
 * imports with identical timestamps).
 *
 * Column ids using dot notation (e.g. `'boundedContext.name'`) are kept as
 * dot-paths in `field` — the DSL accepts them directly for ordering by a
 * relation column (the backend routes the join transparently). No array
 * splitting, unlike the legacy Sequelize `['assoc', 'col', dir]` form.
 *
 * @param sorting - TanStack sorting state
 * @param fallbackColumn - Column to ensure deterministic order (default: 'rowId')
 * @param fallbackDirection - Direction when no sort is active (default: 'desc')
 * @returns DSL order block (e.g., [{ field: 'name', dir: 'asc' }, { field: 'rowId', dir: 'asc' }])
 */
export function buildSortOrder(
  sorting: SortingState,
  fallbackColumn = 'rowId',
  fallbackDirection: OrderDirection = ORDER_DIRECTION.DESC,
): OrderClause[] {
  if (sorting.length === 0) {
    return [{ field: fallbackColumn, dir: fallbackDirection }];
  }

  const order: OrderClause[] = sorting.map((sortEntry) => ({
    field: sortEntry.id,
    dir: sortEntry.desc ? ORDER_DIRECTION.DESC : ORDER_DIRECTION.ASC,
  }));

  // Append fallback if not already in the sort, following the primary direction.
  if (!order.some((entry) => entry.field === fallbackColumn)) {
    order.push({ field: fallbackColumn, dir: order[0].dir });
  }

  return order;
}
